import React, { useEffect, useRef, useState } from 'react';
import { Box, Typography } from '@curves/ui-base';
import { isEmpty } from '@Util/utils';
import useMemberInbodyApi from '@Apis/useMemberInbodyApi';
import { useMeasurementContext } from '@Local/Measurement/Context/MeasurementContext';
import { MeasurementTitle, HtmlFrameComponent } from '@Local/Measurement/Components/Common';
import {
    BodyCurvesProgressSection,
    BodyBalanceSection,
    DiseaseRiskAssessmentSection,
    EvaluationSummarySection,
} from '@Local/Measurement/Components/EvaluationPage';
import { parseDate } from '@Util/moment';
import { useSwitchDisplayMode } from '@Local/Measurement/hook/useSwitchDisplayMode';


const MeasurementEvaluationPage = ({ reportLayout = 'default' }) => {
    const { measureID } = useMeasurementContext();
    const { isTextMode, isGraphMode } = useSwitchDisplayMode();
    const { getMemberMeasureBodyEvaluationApi, getMemberMeasureBodyEvaluationPageApi } = useMemberInbodyApi();
    const [sourceData, setSourceData] = useState(null);
    const [pageData, setPageData] = useState(null);
    const currentMeasureID = useRef(measureID);

    const doMemberMeasureBodyEvaluationApi = async () => {
        if (!isEmpty(sourceData) && currentMeasureID.current === measureID) return;
        const resp = await getMemberMeasureBodyEvaluationApi(measureID);
        if (resp) {
            setSourceData(resp);
            currentMeasureID.current = measureID;
        }
    };

    const doMemberMeasureBodyEvaluationPageApi = async (measureID) => {
        if (!isEmpty(pageData) && currentMeasureID.current === measureID) return;
        const resp = await getMemberMeasureBodyEvaluationPageApi(measureID);
        if (resp) {
            setPageData(resp);
            currentMeasureID.current = measureID;
        }
    };

    useEffect(
        () => {
            if (measureID) {
                isTextMode ? doMemberMeasureBodyEvaluationApi(measureID) : doMemberMeasureBodyEvaluationPageApi(measureID);
            }
        },
        // eslint-disable-next-line
        [measureID, isTextMode],
    );

    let currentTitle, prevTitle;
    if (!isEmpty(sourceData)) {
        currentTitle = parseDate(sourceData?.measureSourceData?.measureDate, 'M月');
        prevTitle = parseDate(sourceData?.prevMeasureSourceData?.measureDate, 'M月');

        if (currentTitle === prevTitle) {
            currentTitle = parseDate(sourceData?.measureSourceData?.measureDate, 'M月D日');
            prevTitle = parseDate(sourceData?.prevMeasureSourceData?.measureDate, 'M月D日');
        }
    }

    return (
        <>
            {reportLayout === 'default' && isTextMode && !isEmpty(sourceData) && (
                <Box className={'container measurement-transcript'}>
                    <Box className={'measurement-group px-3 pb-3'}>
                        <MeasurementTitle title={'綜合數據評估'} />
                        <EvaluationSummarySection
                            currentTitle={currentTitle}
                            prevTitle={prevTitle}
                            measureSourceData={sourceData?.measureSourceData}
                            prevMeasureSourceData={sourceData?.prevMeasureSourceData}
                            compareData={sourceData?.compareData}
                        />
                    </Box>
                    <Box className={'measurement-group px-3 pb-3'}>
                        <MeasurementTitle title={'疾病風險評估'} />
                        <Typography className={'font-color-3'} variant={'body2'}>
                            以下相關數據不能作為疾病的診斷；如有疑慮，建議須再諮詢相關專業醫師。
                        </Typography>
                        <DiseaseRiskAssessmentSection
                            currentTitle={currentTitle}
                            prevTitle={prevTitle}
                            measureSourceData={sourceData?.measureSourceData}
                            prevMeasureSourceData={sourceData?.prevMeasureSourceData}
                            compareData={sourceData?.compareData}
                        />
                    </Box>
                    <Box className={'measurement-group px-3 pb-3'}>
                        <MeasurementTitle title={'曲線變化評估'} />
                        <BodyCurvesProgressSection
                            notifyNeckMC={sourceData?.compareData?.notifyNeckMC}
                            notifyArmMC={sourceData?.compareData?.notifyArmMC}
                            notifyChestMC={sourceData?.compareData?.notifyChestMC}
                            notifyAbdomenMC={sourceData?.compareData?.notifyAbdomenMC}
                            notifyHipMC={sourceData?.compareData?.notifyHipMC}
                            notifyThighMC={sourceData?.compareData?.notifyThighMC}
                            signNeckMC={sourceData?.compareData?.signNeckMC}
                            signArmMC={sourceData?.compareData?.signArmMC}
                            signChestMC={sourceData?.compareData?.signChestMC}
                            signAbdomenMC={sourceData?.compareData?.signAbdomenMC}
                            signHipMC={sourceData?.compareData?.signHipMC}
                            signThighMC={sourceData?.compareData?.signThighMC}
                        />
                    </Box>
                    <Box className={'measurement-group px-3 pb-3'}>
                        <MeasurementTitle title={'身體平衡評估'} />
                        <BodyBalanceSection
                            balancedImageNames={sourceData?.compareData?.balancedImageNames}
                            balancedLimbsTexts={sourceData?.compareData?.balancedLimbsTexts}
                            balancedBodyTexts={sourceData?.compareData?.balancedBodyTexts}
                        />
                    </Box>
                </Box>
            )}
            {isGraphMode && !isEmpty(pageData) && (
                pageData.hasData ? (
                    <>
                        <Box className={'container-full page-center'}>
                            <HtmlFrameComponent title={'InBody 身體評估表'} pageUrl={pageData.pageUrl} floatButtonClass="" />
                        </Box>
                    </>
                ) : (
                    <Box className={'container page-center'}>
                        <Box className={'measurement-empty px-3 py-2'}>暫無<strong>身體評估表</strong>內容</Box>
                    </Box>
                )
            )}
        </>
    );
};

export default MeasurementEvaluationPage;
